import './styles.scss'
import React, { useState, useEffect, useRef, useCallback, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Chart } from 'primereact/chart'
import { Chart as ChartJS } from 'chart.js'
import { CrosshairPlugin } from 'chartjs-plugin-crosshair'
import CustomDropdown from 'Components/CustomDropdown'
import { getLocalTheme, getLocalUserDetails } from 'Utils/LocalStorage_Handler'
import { InputText } from 'primereact/inputtext'
import { Toast } from 'primereact/toast'
import { commonMethods } from 'Utils/commonMethods'
import { Skeleton } from 'primereact/skeleton'
import Lottie from 'lottie-react'
import { useHistory, useLocation } from 'react-router-dom'
import { ROUTE_STRINGS } from 'Utils/Constants'
import { UnFilledHeartIcon, FilledHeartIcon } from 'Assets/Icons'
import { SKELETON_COUNT } from './Utils/Constants'
import { WEATHER_METHODS } from './Utils/methods'
import { fetchSearchResults, fetchWeatherDataByCountry, saveLocationForUser, fetchUserSavedLocations, deleteLocationForUser, fetchUniqueStatesData, fetchUniqueRegionsData, fetchUniqueCountriesData, fetchFortNightWeatherData, fetchHourlyWeatherData } from './Services'

const HorizontalCrosshairPlugin = {
  id: 'horizontalCrosshair',
  afterEvent(chart, args) {
    const e = args?.event
    const area = chart?.chartArea

    if (!e || !area || e.type === 'mouseout') {
      // eslint-disable-next-line
      chart.$horizontalCrosshair = null

      return
    }

    const isInsideChartArea = e.x >= area.left && e.x <= area.right && e.y >= area.top && e.y <= area.bottom

    // eslint-disable-next-line
    chart.$horizontalCrosshair = isInsideChartArea ? { y: e.y } : null
  },
  afterDraw(chart, _args, options) {
    const pos = chart.$horizontalCrosshair
    const area = chart?.chartArea

    if (!pos || !area) return

    const lineOptions = options?.line || {}
    const color = lineOptions.color ?? 'rgba(255, 255, 255, 0.6)'
    const width = lineOptions.width ?? 1
    const dashPattern = lineOptions.dashPattern ?? [6, 6]

    const { ctx } = chart

    ctx.save()
    ctx.beginPath()
    ctx.setLineDash(dashPattern)
    ctx.lineWidth = width
    ctx.strokeStyle = color
    ctx.moveTo(area.left, pos.y)
    ctx.lineTo(area.right, pos.y)
    ctx.stroke()
    ctx.restore()
  }
}

ChartJS.register(CrosshairPlugin, HorizontalCrosshairPlugin)

const LocationCard = memo(({ item, isActive, onLocationClick, getCurrentDateForTimezone, getCurrentTimeForTimezone, getWeatherConditionIcon, getWeatherLottieAsset, onAddLocation, onRemoveLocation, userDetails, weatherData, setWeatherData }) => {
  const locationId = item?.location_id || item?.id
  const weatherCondition = item?.weather?.weather_condition
  const locationDateTime = item?.timezone ? new Date(new Date().toLocaleString('en-US', { timeZone: item.timezone })) : new Date()

  const conditionIcon = getWeatherConditionIcon(weatherCondition, locationDateTime)
  const isConditionIconImage = typeof conditionIcon === 'string'

  const history = useHistory()

  const backgroundAnimation = getWeatherLottieAsset(item?.weather?.weather_condition, locationDateTime)
  const isBackgroundImage = typeof backgroundAnimation === 'string'
  const handleClick = (event, item) => {
    const updatedValue = !item?.isInUserLocation

    if (!userDetails) {
      history.push(ROUTE_STRINGS.login)
    }
    setWeatherData((prevData) => prevData?.map((loc) => (loc.location_id === item.location_id ? { ...loc, isInUserLocation: updatedValue } : loc)))

    if (item?.isInUserLocation) {
      onRemoveLocation(userDetails?.id, item?.location_id, item?.location_name, false, event)
    } else {
      onAddLocation(event, item, false)
    }
  }

  return (
    <div className={`location-card ${isActive ? 'active' : ''}`} onClick={() => onLocationClick(locationId)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onLocationClick(locationId)}>
      <div className="location-card-background">{isBackgroundImage ? <img src={backgroundAnimation} alt="Weather background" className="weather-background-animation" /> : <Lottie animationData={backgroundAnimation} loop={true} className="weather-background-animation" />}</div>
      <div className="location-card-content">
        <div className="location-card-header">
          <div className="location-date-time-wrapper">
            <div className="location-date-time-container">
              <div className="location-date">{getCurrentDateForTimezone(item?.timezone, 'spaceFormat')}</div>
              <div className="location-date">{getCurrentTimeForTimezone(item?.timezone)}</div>
            </div>
          </div>

          <div className="location-condition-icon">{isConditionIconImage ? <img src={conditionIcon} alt={weatherCondition || 'Weather'} className="weather-animation-small" /> : <Lottie animationData={conditionIcon} loop={true} className="weather-animation-small" />}</div>

          <div className="location-temp-large">
            <div className="location-radio-wrapper">{isActive && item?.weather?.temperature && <input type="radio" name="location" value={item?.location_name} defaultChecked />}</div>
            <div className="location-temp-large-value"> {item?.weather?.temperature ? `${Number(item.weather.temperature).toFixed(0)}°C` : '-'}</div>
          </div>
        </div>

        <div className="location-card-body">
          <div></div> {/* for ui purposes */}
          <div className="location-city">
            {item.location_name}
            {item?.zone && <span className="location-zone">Zone: {item?.zone}</span>}
          </div>
          <div className="add-otherlocation">
            <i className="heart-icon" onClick={(event) => handleClick(event, item)}>
              {item?.isInUserLocation ? <FilledHeartIcon /> : <UnFilledHeartIcon />}
            </i>
          </div>
        </div>

        <div className="location-card-footer">
          <div className="location-condition">{item?.weather?.weather_condition}</div>
          <div className="location-high-low">
            H:{item?.weather?.high_temperature ? `${Number(item?.weather?.high_temperature).toFixed(0)}°C` : '-'} L:{item?.weather?.low_temperature ? `${Number(item?.weather?.low_temperature).toFixed(0)}°C` : '-'}
          </div>
        </div>
      </div>
    </div>
  )
})

LocationCard.displayName = 'LocationCard'

const WeatherLocationsColumn = memo(({ isLoadingLocations, isSearching, isOtherOptionsOpen, selectedCountry, selectedState, selectedRegion, weatherData, setWeatherData, currentCity, otherLocationsData, savedLocationsFound, searchQuery, userDetails, onLocationClick, onCountrySearch, onAddLocation, onRemoveLocation, getCountryFlag, getCurrentDateForTimezone, getCurrentTimeForTimezone, getWeatherConditionIcon, getWeatherLottieAsset }) => {
  const LocationCardSkeletonInner = () => (
    <div className="location-card skeleton-card">
      <div className="location-card-header">
        <div className="location-date-time-wrapper">
          <Skeleton width="80px" height="18px" className="skeleton-text custom-skelton" />
          <Skeleton width="60px" height="18px" className="skeleton-text custom-skelton" />
        </div>
        <div className="location-flag-wrapper">
          <Skeleton width="60px" height="18px" className="skeleton-text custom-skelton" />
        </div>
        <Skeleton width="80px" height="48px" className="skeleton-temp custom-skelton" />
      </div>
      <div className="location-card-body">
        <Skeleton width="150px" height="18px" className="skeleton-text custom-skelton" />
        <Skeleton width="100px" height="16px" className="skeleton-text custom-skelton" />
      </div>
      <div className="location-card-footer">
        <Skeleton width="100px" height="16px" className="skeleton-text custom-skelton" />
        <Skeleton width="80px" height="18px" className="skeleton-text custom-skelton" />
        <Skeleton width="80px" height="18px" className="skeleton-text custom-skelton" />
      </div>
    </div>
  )

  if (isOtherOptionsOpen) {
    return (
      <div className="weather-locations-column other-locations-column">
        <div className="locations-list other-locations-list">
          {isLoadingLocations && !isSearching ? (
            <>
              <div className="location-card-header-skeleton">
                <Skeleton width="90%" height="18px" className="skeleton-text custom-skelton" />
              </div>
              {Array.from({ length: SKELETON_COUNT.LOCATION_CARD }).map((_, index) => (
                <LocationCardSkeletonInner key={index} />
              ))}
            </>
          ) : (
            <>
              <div className="weather-search-container">
                <div className="search-input-wrapper p-input-icon-left">
                  <i className="pi pi-search" data-testid="search-icon" />
                  <InputText type="text" name="search-city" id="search-city-input" placeholder="Search City" value={searchQuery} onChange={onCountrySearch} className="weather-search-input" />
                  {isSearching && <i className="pi pi-spinner pi-spin" style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }} />}
                </div>
              </div>
              {isSearching ? (
                <>
                  {Array.from({ length: SKELETON_COUNT.LOCATION_CARD }).map((_, index) => (
                    <LocationCardSkeletonInner key={index} />
                  ))}
                </>
              ) : (
                <>
                  {savedLocationsFound ? (
                    <>
                      {otherLocationsData?.map((item) => {
                        const locationDateTime = item?.timezone ? new Date(new Date().toLocaleString('en-US', { timeZone: item.timezone })) : new Date()

                        const backgroundAnimation = getWeatherLottieAsset(item?.weather?.weather_condition, locationDateTime)
                        const isBackgroundImage = typeof backgroundAnimation === 'string'

                        return (
                          <div key={item.id} className={`location-card ${currentCity === item?.location_name ? 'active' : ''} other-locations-card`} onClick={() => onLocationClick(item?.location_id || item?.id)}>
                            <div className="location-card-background">{isBackgroundImage ? <img src={backgroundAnimation} alt="Weather background" className="weather-background-animation" /> : <Lottie animationData={backgroundAnimation} loop={true} className="weather-background-animation" />}</div>
                            <div className="location-card-content">
                              <div className="location-card-header">
                                <div className="location-date-time-wrapper">
                                  <div className="location-date-time-container">
                                    <div className="location-date">{getCurrentDateForTimezone(item?.timezone, 'spaceFormat')}</div>
                                    <div className="location-date">{getCurrentTimeForTimezone(item?.timezone)}</div>
                                  </div>
                                </div>
                                <div className="location-flag-wrapper">
                                  <img src={getCountryFlag(item?.country_code)} alt={item?.country_name} className="location-flag" />
                                  <span className="location-country">{item?.country_name}</span>
                                </div>
                                <div className="location-temp-large">
                                  <div className="location-radio-wrapper">{currentCity === item?.location_name && <input type="radio" name="location" value={item?.location_name} defaultChecked />}</div>
                                  <div className="location-temp-large-value">{Number(item?.weather?.temperature).toFixed(0)}°C</div>
                                </div>
                              </div>
                              <div className="location-card-body">
                                <div className="add-remove-btn">
                                  {item?.isUserSaved ? (
                                    <div className="remove-location-btn" onClick={(e) => onRemoveLocation(userDetails?.id, item?.location_id, item?.location_name, true, e)}>
                                      <i className="pi pi-minus-circle" />
                                    </div>
                                  ) : (
                                    <div className="add-location-btn" onClick={(e) => onAddLocation(e, item, true)}>
                                      <i className="pi pi-plus-circle" />
                                    </div>
                                  )}
                                </div>
                                <div className="location-city">
                                  {item?.location_name}
                                  {item?.zone && <span className="location-zone">Zone: {item?.zone}</span>}
                                </div>
                                <div></div>
                              </div>

                              <div className="location-card-footer other-locations-card-footer">
                                <div className="location-condition">{item?.weather?.weather_condition}</div>
                                <div></div>
                                <div className="location-high-low">
                                  H:{Number(item?.weather?.high_temperature).toFixed(0)}°C L:{Number(item?.weather?.low_temperature).toFixed(0)}°C
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </>
                  ) : (
                    <div className="weather-card no-data-card">
                      <div className="no-data-icon">
                        <i className="pi pi-search" />
                      </div>
                      <div className="no-data-message">Please search and add a location to get started.</div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="weather-locations-column">
      <div className="locations-list">
        {isLoadingLocations ? (
          <>
            <div className="location-card-header-skeleton">
              <Skeleton width="90%" height="18px" className="skeleton-text custom-skelton" />
            </div>
            {Array.from({ length: SKELETON_COUNT.LOCATION_CARD }).map((_, index) => (
              <LocationCardSkeletonInner key={index} />
            ))}
          </>
        ) : (
          <>
            <div className="state-block">
              <div className="coffee-producing-header">
                <div className="coffee-producing-content">
                  <img src={getCountryFlag(selectedCountry?.country_code)} alt={selectedCountry?.country_name} className="location-flag" />
                  <div className="coffee-producing-header-content-text">
                    <span className="location-country">
                      {selectedCountry?.country_name} {selectedState?.state && `- ${selectedState?.state}`}
                      {selectedRegion?.region && `, ${selectedRegion?.region}`}
                    </span>
                  </div>
                </div>
              </div>
              {weatherData.map((item) => (
                <LocationCard key={item?.location_id || item?.id} item={item} isActive={currentCity === item?.location_name} onLocationClick={onLocationClick} getCurrentDateForTimezone={getCurrentDateForTimezone} getCurrentTimeForTimezone={getCurrentTimeForTimezone} getWeatherConditionIcon={getWeatherConditionIcon} getWeatherLottieAsset={getWeatherLottieAsset} onAddLocation={onAddLocation} onRemoveLocation={onRemoveLocation} userDetails={userDetails} weatherData={weatherData} setWeatherData={setWeatherData} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
})

WeatherLocationsColumn.displayName = 'WeatherLocationsColumn'

const Weather = () => {
  const { t } = useTranslation()
  const userDetails = getLocalUserDetails()
  const {
    timeMethods: { getUtcTime, getDayOfWeek, getHourlyTime, getCurrentTimeForTimezone, getCurrentDateForTimezone, convertTo24Hour },
    renderMethods: { getWeatherConditionIcon, getCountryFlag, setUrlParam, getWeatherLottieAsset }
  } = WEATHER_METHODS

  const history = useHistory()

  const uniqueStatesDataRef = useRef([])
  const uniqueRegionsDataRef = useRef([])
  const userSavedLocationsRef = useRef([])
  const toast = useRef(null)
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const type = params.get('type')

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCountry, setSelectedCountry] = useState(null)
  const [selectedState, setSelectedState] = useState(null)
  const [selectedRegion, setSelectedRegion] = useState(null)
  const [isOtherOptionsOpen, setIsOtherOptionsOpen] = useState(false)

  const [uniqueCountries, setUniqueCountries] = useState([])
  const [uniqueStates, setUniqueStates] = useState([])
  const [uniqueRegions, setUniqueRegions] = useState([])
  const [weatherData, setWeatherData] = useState([])
  const [fortNightForecast, setFortNightForecast] = useState([])
  const [hourlyForecast, setHourlyForecast] = useState([])
  const [userSavedLocations, setUserSavedLocations] = useState([])

  const [isLoadingLocations, setIsLoadingLocations] = useState(true)
  const [isLoadingForecast, setIsLoadingForecast] = useState(true)
  const [isLoadingHourly, setIsLoadingHourly] = useState(true)
  const [isSearching, setIsSearching] = useState(false)

  const [currentDay, setCurrentDay] = useState('Today')
  const [currentCity, setCurrentCity] = useState('')
  const [currentForecast, setCurrentForecast] = useState('')
  const [theme, setTheme] = useState(getLocalTheme() || 'light')
  const [otherLocationsData, setOtherLocationsData] = useState(userSavedLocationsRef.current)
  const [savedLocationsFound, setSavedLocationsFound] = useState(true)

  const temperatureData = {
    labels: hourlyForecast.map((item) => getHourlyTime(item.forecast_time)),
    datasets: [
      {
        label: 'Temperature',
        data: hourlyForecast.map((item) => item.temperature),
        borderColor: '#ff9500',
        backgroundColor: 'rgba(255, 149, 0, 0.3)',
        tension: 0.4,
        fill: true
        // pointRadius: 6,
        // pointBackgroundColor: '#ff9500',
        // pointBorderColor: '#fff',
        // pointBorderWidth: 2
      }
    ]
  }

  const chartOptions = {
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: { size: 14 },
        bodyFont: { size: 12 },
        padding: 12,
        displayColors: false,
        callbacks: {
          label(context) {
            return `${context.parsed.y}°C`
          }
        }
      },
      crosshair: {
        line: {
          color: 'rgba(255, 255, 255, 0.6)',
          width: 1,
          dashPattern: [6, 6]
        },
        zoom: { enabled: false }
      },
      horizontalCrosshair: {
        line: {
          color: 'rgba(255, 255, 255, 0.6)',
          width: 1,
          dashPattern: [6, 6]
        }
      }
    },
    scales: {
      x: {
        ticks: {
          color: '#fff',
          font: { size: 12 }
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      },
      y: {
        position: 'right',
        ticks: {
          color: '#fff',
          font: { size: 12 },
          callback(value) {
            return `${value}°C`
          }
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
          drawBorder: false
        }
      }
    },
    elements: {
      point: { hoverRadius: 8 }
    }
  }

  useEffect(() => {
    const updateTheme = () => {
      const theme = getLocalTheme()

      setTheme(theme)
    }

    window.addEventListener('themeChanged', updateTheme)

    return () => {
      window.removeEventListener('themeChanged', updateTheme)
    }
  }, [])

  useEffect(() => {
    fetchUniqueCountriesFunction()
  }, [])

  useEffect(() => {
    if (!type) {
      const upadatedParam = setUrlParam(location.search, 'coffee-weather')

      history.push({ search: upadatedParam.toString() })
      fetchUniqueCountriesFunction()
    } else if (type === 'coffee-weather') {
      setIsOtherOptionsOpen(false)
    } else if (type === 'other-location') {
      setIsOtherOptionsOpen(true)
      setSearchQuery('')
      fetchUserSavedLocationsFunction()
    }
  }, [location.search])

  const fetchUserSavedLocationsFunction = async () => {
    setIsLoadingLocations(true)
    setIsLoadingForecast(true)
    setIsLoadingHourly(true)

    try {
      const { data, error } = await fetchUserSavedLocations(userDetails?.id)

      if (error) {
        console.error('Error fetching user saved locations:', error)

        return
      }

      if (data?.length) {
        const savedWeatherData = await fetchWeatherDataByCountryFunction(0, data, null, null, true)
        const updatedSavedWeatherData = savedWeatherData.map((item) => ({ ...item, isUserSaved: true }))

        userSavedLocationsRef.current = updatedSavedWeatherData
        setUserSavedLocations(updatedSavedWeatherData)
        setOtherLocationsData(updatedSavedWeatherData)
        setSavedLocationsFound(true)
        setCurrentCity(updatedSavedWeatherData[0]?.location_name)
        fetchFortNightWeatherDataFunction(updatedSavedWeatherData[0]?.location_id || updatedSavedWeatherData[0]?.id)
      } else {
        setSavedLocationsFound(false)
        setOtherLocationsData([])
        setCurrentCity('')
        setHourlyForecast([])
        setFortNightForecast([])
        setIsLoadingLocations(false)
        setIsLoadingForecast(false)
        setIsLoadingHourly(false)
      }
    } catch (error) {
      console.error('Error in fetchUserSavedLocationsFunction:', error)
      setIsLoadingLocations(false)
      setIsLoadingForecast(false)
      setIsLoadingHourly(false)
    } finally {
      setIsLoadingLocations(false)
    }
  }

  const fetchUniqueCountriesFunction = async () => {
    setIsLoadingLocations(true)
    setIsLoadingForecast(true)
    setIsLoadingHourly(true)

    try {
      // Countries (with flagURL from updated service)
      const uniqueCountriesData = await fetchUniqueCountriesData()

      if (!uniqueCountriesData?.length) {
        setUniqueCountries([])
        setUniqueStates([])
        setUniqueRegions([])
        setSelectedCountry(null)
        setSelectedState(null)
        setSelectedRegion(null)

        return
      }

      // Prefer user's country if present, otherwise first country
      const userCountry = uniqueCountriesData.filter((el) => el?.country_id === userDetails?.countryId)
      const userSelectedCountry = userCountry?.length > 0 ? userCountry : uniqueCountriesData
      const country = userSelectedCountry[0]

      setSelectedCountry(country)
      setUniqueCountries(uniqueCountriesData)

      // States for selected country
      const uniqueStatesData = await fetchUniqueStatesData(country?.country_id)

      uniqueStatesDataRef.current = uniqueStatesData

      // Default state = first row from states RPC
      const defaultState = uniqueStatesData[0] ? { state: uniqueStatesData[0].state } : null

      setSelectedState(defaultState)

      // Regions for default state
      let uniqueRegionsData = []
      let defaultRegion = null

      if (defaultState?.state) {
        uniqueRegionsData = await fetchUniqueRegionsData(country?.country_id, uniqueStatesData[0].state_name || defaultState.state)
        uniqueRegionsDataRef.current = uniqueRegionsData

        // Default region = first row from regions RPC
        if (uniqueRegionsData[0]) {
          defaultRegion = { region: uniqueRegionsData[0].region }
        }
      }

      setSelectedRegion(defaultRegion)

      setUniqueStates(uniqueStatesData.map((item) => ({ state: item.state })))
      setUniqueRegions(uniqueRegionsData.map((item) => ({ region: item.region })))

      // Fetch weather data with default state & region
      fetchWeatherDataByCountryFunction(country, [], defaultState?.state, defaultRegion?.region)
    } catch (error) {
      console.error('Error in fetchUniqueCountriesFunction:', error)
      setIsLoadingLocations(false)
      setIsLoadingForecast(false)
      setIsLoadingHourly(false)
    }
  }

  const fetchWeatherDataByCountryFunction = async (countryData, userSearchLocations = [], state = null, region = null, isSavedLocation = false) => {
    try {
      setIsLoadingLocations(true)
      setIsLoadingForecast(true)
      setIsLoadingHourly(true)

      const weatherDataResponse = await fetchWeatherDataByCountry(countryData?.country_id, userSearchLocations, state, region)

      setWeatherData(weatherDataResponse)
      setCurrentCity(weatherDataResponse[0]?.location_name)

      if (weatherDataResponse?.length) {
        if (!isSavedLocation) {
          const fortNightlyLocationId = userSearchLocations?.length > 0 ? weatherDataResponse[0]?.id : weatherDataResponse[0]?.location_id

          fetchFortNightWeatherDataFunction(fortNightlyLocationId)
        }
      }

      return weatherDataResponse
    } catch (error) {
      console.error('Error fetching weather data by country:', error)
      setIsLoadingForecast(false)
      setIsLoadingHourly(false)
    } finally {
      setIsLoadingLocations(false)
    }
  }

  const fetchFortNightWeatherDataFunction = async (locationId) => {
    setIsLoadingForecast(true)
    setIsLoadingHourly(true)

    try {
      const fortNightResponse = await fetchFortNightWeatherData(locationId)

      const updatedFortNightResponse = fortNightResponse?.data?.map((el) => ({ ...el, location_id: locationId }))

      setFortNightForecast(updatedFortNightResponse || [])

      if (updatedFortNightResponse.length > 0) {
        setCurrentForecast(updatedFortNightResponse[0]?.forecast_date)
        fetchHourlyDataFunction(locationId, updatedFortNightResponse[0].forecast_date)
      } else {
        // No forecast data for this location – clear any previously shown hourly data
        setCurrentForecast('')
        setCurrentDay('Today')
        setHourlyForecast([])
        setIsLoadingHourly(false)
      }
    } catch (error) {
      console.error('Error fetching fort night weather data:', error)
      setIsLoadingHourly(false)
    } finally {
      setIsLoadingForecast(false)
    }
  }

  const fetchHourlyDataFunction = async (locationId, forecastDate) => {
    setIsLoadingHourly(true)
    try {
      const hourlyResponse = await fetchHourlyWeatherData(locationId, forecastDate)

      setCurrentDay(getDayOfWeek(forecastDate))
      setHourlyForecast(hourlyResponse?.data || [])
    } catch (error) {
      console.error('Error fetching hourly weather data:', error)
    } finally {
      setIsLoadingHourly(false)
    }
  }

  const handleFortNightForecastClick = (locationId, forecastDate) => {
    if (currentForecast === forecastDate) return
    setCurrentForecast(forecastDate)
    fetchHourlyDataFunction(locationId, forecastDate)
  }

  const getWeatherIcon = (iconType) => {
    const iconClass =
      {
        sun: 'pi pi-sun',
        cloud: 'pi pi-cloud',
        'sun-cloud': 'pi pi-cloud-sun',
        'cloud-rain': 'pi pi-cloud-rain',
        'cloud-moon': 'pi pi-moon'
      }[iconType] || 'pi pi-circle'

    return <i className={iconClass} />
  }

  const handleLocationClick = useCallback(
    (locationId) => {
      let clickedLocationName = ''

      if (isOtherOptionsOpen) {
        clickedLocationName = otherLocationsData.find((item) => item?.location_id === locationId || item?.id === locationId)?.location_name
      } else {
        clickedLocationName = weatherData.find((item) => item?.location_id === locationId || item?.id === locationId)?.location_name
      }
      if (currentCity === clickedLocationName) return
      setCurrentCity(clickedLocationName)
      fetchFortNightWeatherDataFunction(locationId)
    },
    [currentCity, isOtherOptionsOpen, otherLocationsData, weatherData]
  )

  const handleOtherOption = () => {
    const optionValue = !isOtherOptionsOpen

    setIsLoadingLocations(true)
    setIsLoadingForecast(true)
    setIsLoadingHourly(true)

    setOtherLocationsData([])
    setWeatherData([])
    setFortNightForecast([])
    setHourlyForecast([])
    setCurrentForecast('')
    setCurrentDay('Today')
    setSearchQuery('')

    setIsOtherOptionsOpen(optionValue)

    if (optionValue === false) {
      fetchWeatherDataByCountryFunction(selectedCountry, [], selectedState?.state, selectedRegion?.region)
    }

    if (userDetails?.id) {
      const value = optionValue ? 'other-location' : 'coffee-weather'
      const updatedValue = setUrlParam(location.search, value)

      history.push({ search: updatedValue.toString() })
    } else {
      history.push(ROUTE_STRINGS.login)
      setIsOtherOptionsOpen(false)
    }

    // commented for the reference
    // else {
    //   setSearchQuery('')

    //   if (!userDetails?.id) {
    //     history.push(ROUTE_STRINGS.login)
    //     setIsOtherOptionsOpen(false)

    //     return
    //   }
    //   fetchUserSavedLocationsFunction()
    // }
  }

  const handleDropdownChange = async (e, type) => {
    // Set loading states FIRST to show skeletons instead of old data
    setIsLoadingLocations(true)
    setIsLoadingForecast(true)
    setIsLoadingHourly(true)

    if (type === 'country') {
      const selectedCountryValue = e.target.value

      setSelectedCountry(selectedCountryValue)
      setIsOtherOptionsOpen(false)

      if (isOtherOptionsOpen) {
        const updated = setUrlParam(location.search, 'coffee-weather')

        history.push({ search: updated.toString() })
      }
      // Clear previous data after setting loading states
      setWeatherData([])
      setFortNightForecast([])
      setHourlyForecast([])
      setCurrentForecast('')
      setCurrentDay('Today')

      // Fetch states for the selected country
      const statesData = await fetchUniqueStatesData(selectedCountryValue?.country_id)

      uniqueStatesDataRef.current = statesData

      setUniqueStates(statesData.map((item) => ({ state: item.state })))

      // Default state = first state from response
      const defaultState = statesData[0] ? { state: statesData[0].state } : null

      setSelectedState(defaultState)

      // Fetch regions for default state
      let regionsData = []
      let defaultRegion = null

      if (defaultState?.state) {
        regionsData = await fetchUniqueRegionsData(selectedCountryValue?.country_id, statesData[0].state_name || defaultState.state)

        uniqueRegionsDataRef.current = regionsData

        setUniqueRegions(regionsData.map((item) => ({ region: item.region })))

        // Default region = first region from response
        if (regionsData[0]) {
          defaultRegion = { region: regionsData[0].region }
        }
      } else {
        setUniqueRegions([])
      }

      setSelectedRegion(defaultRegion)

      // Fetch weather data with country, default state, and default region
      fetchWeatherDataByCountryFunction(selectedCountryValue, [], defaultState?.state, defaultRegion?.region)
    } else if (type === 'state') {
      const newState = e.target.value

      setSelectedState(newState)

      // Clear previous data after setting loading states
      setWeatherData([])
      setFortNightForecast([])
      setHourlyForecast([])
      setCurrentForecast('')
      setCurrentDay('Today')

      // Fetch regions for the selected state
      const regionsData = await fetchUniqueRegionsData(selectedCountry?.country_id, newState?.state)

      uniqueRegionsDataRef.current = regionsData

      const regionsForDropdown = regionsData.map((item) => ({ region: item.region }))

      setUniqueRegions(regionsForDropdown)

      // Default region = first region from response
      const defaultRegion = regionsForDropdown[0] || null

      setSelectedRegion(defaultRegion)

      // Fetch weather data with country, selected state, and default region
      fetchWeatherDataByCountryFunction(selectedCountry, [], newState?.state, defaultRegion?.region)
    } else if (type === 'region') {
      setSelectedRegion(e.target.value)

      // Set loading states and clear previous data
      setIsLoadingLocations(true)
      setIsLoadingForecast(true)
      setIsLoadingHourly(true)
      setWeatherData([])
      setFortNightForecast([])
      setHourlyForecast([])
      setCurrentForecast('')
      setCurrentDay('Today')

      // Fetch weather data with country, selected state, and selected region
      fetchWeatherDataByCountryFunction(selectedCountry, [], selectedState?.state, e.target.value?.region)
    }
  }

  const handleCountrySearch = useCallback((e) => {
    const value = e.target.value

    if (value === '') {
      // cancel any pending debounced search so stale calls don't run
      if (debouncedSearch?.cancel) debouncedSearch.cancel()

      setSearchQuery('')
      setOtherLocationsData(userSavedLocationsRef.current)
      setSavedLocationsFound(userSavedLocationsRef.current?.length > 0)

      // Reset forecast data and current city when clearing search
      if (userSavedLocationsRef.current?.length > 0) {
        const firstLocation = userSavedLocationsRef.current[0]

        setCurrentCity(firstLocation?.location_name)
        fetchFortNightWeatherDataFunction(firstLocation?.location_id || firstLocation?.id)
      } else {
        setCurrentCity('')
        setFortNightForecast([])
        setHourlyForecast([])
        setCurrentForecast('')
        setCurrentDay('Today')
      }

      return
    }

    setSearchQuery(value)
  }, [])

  const debounce = (func, delay) => {
    let timeoutId

    const debouncedFunc = (...args) => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => func(...args), delay)
    }

    // A cancel method to the debounced function
    debouncedFunc.cancel = () => {
      clearTimeout(timeoutId)
    }

    return debouncedFunc
  }

  const debouncedSearch = useRef(
    debounce(async (value) => {
      if (!value || value.trim() === '') {
        setOtherLocationsData(userSavedLocationsRef.current)
        setSearchQuery('')

        setSavedLocationsFound(userSavedLocationsRef.current?.length > 0)

        return
      }

      try {
        setIsSearching(true)
        const userLocationsResponse = await fetchSearchResults(value)

        // If search returned no results
        if (!userLocationsResponse?.data || userLocationsResponse.data.length === 0) {
          setOtherLocationsData([])
          setSavedLocationsFound(false)
          setCurrentCity('')
          setFortNightForecast([])
          setHourlyForecast([])
          setIsSearching(false)

          return
        }

        const locationWeatherData = await fetchWeatherDataByCountryFunction(selectedCountry, userLocationsResponse.data)
        const currentSavedLocations = userSavedLocationsRef.current
        const savedLocationNames = new Set(currentSavedLocations.map((loc) => loc.location_name.toLowerCase().trim()))

        const updatedLocationWeatherData = locationWeatherData?.map((item) => (savedLocationNames.has(item.location_name.toLowerCase()) ? { ...item, isUserSaved: true } : { ...item, location_id: item.id }))

        setOtherLocationsData(updatedLocationWeatherData)
        setSavedLocationsFound(updatedLocationWeatherData?.length > 0)
      } catch (error) {
        console.error('Error searching locations:', error)
        // On error, show saved locations
        setOtherLocationsData(userSavedLocationsRef.current)
      } finally {
        setIsSearching(false)
      }
    }, 300)
  ).current

  useEffect(() => {
    // call debounced search when the controlled value changes
    if (searchQuery === '') {
      // cancel any pending debounced search to avoid receiving stale chars
      if (debouncedSearch?.cancel) debouncedSearch.cancel()

      setOtherLocationsData(userSavedLocationsRef.current)
      setIsSearching(false)
      setSavedLocationsFound(userSavedLocationsRef.current?.length > 0)

      return
    }

    debouncedSearch(searchQuery)
  }, [searchQuery, debouncedSearch])

  const handleAddLocation = useCallback(
    async (e, location, isInOtherLocation) => {
      e.stopPropagation()

      try {
        const updatedLocation = { ...location, isUserSaved: true }

        setUserSavedLocations((prev) => [updatedLocation, ...prev])
        userSavedLocationsRef.current = [updatedLocation, ...userSavedLocationsRef.current]
        setOtherLocationsData([updatedLocation, ...userSavedLocations])

        if (isInOtherLocation) {
          setCurrentCity(updatedLocation?.location_name)
          fetchFortNightWeatherDataFunction(updatedLocation?.location_id)
        }

        setSearchQuery('')
        await saveLocationForUser(userDetails.id, location.id, location.location_name, location?.latitude, location?.longitude, location?.country_code, location?.state, location?.region, location?.country_name, location?.zone, location?.country_id)

        toast.current?.show({
          severity: 'success',
          detail: `${location.location_name} ${t('HAS_BEEN_ADDED_TO_YOUR_LOCATIONS') || 'has been added to your locations'}`,
          life: 4000
        })
      } catch (error) {
        console.error('Error saving location:', error)
        toast.current?.show({
          severity: 'error',
          detail: t('FAILED_TO_ADD_LOCATION') || 'Failed to add location. Please try again.',
          life: 4000
        })
      }
    },
    [userDetails?.id, userSavedLocations, t]
  )

  const handleRemoveLocation = useCallback(
    async (userId, locationId, locationName, flag, e) => {
      e.stopPropagation()

      try {
        await deleteLocationForUser(userId, locationId)
        setUserSavedLocations((prev) => prev.filter((item) => item.location_id !== locationId))
        userSavedLocationsRef.current = userSavedLocationsRef.current.filter((item) => item.location_id !== locationId)

        const updatedOtherLocationsData = otherLocationsData.filter((item) => item.location_id !== locationId)
        const hasSavedLocations = userSavedLocationsRef.current?.length > 0

        if (!hasSavedLocations) {
          setSavedLocationsFound(hasSavedLocations)
          setOtherLocationsData(userSavedLocationsRef.current)
          if (flag) {
            setCurrentCity('')
            setFortNightForecast([])
            setHourlyForecast([])
            setCurrentForecast('')
          }
          setCurrentDay('Today')
        } else {
          if (flag) {
            setCurrentCity(updatedOtherLocationsData[0]?.location_name)
            fetchFortNightWeatherDataFunction(updatedOtherLocationsData[0]?.location_id)
          }
          setOtherLocationsData(updatedOtherLocationsData)
        }

        toast.current?.show({
          severity: 'success',
          detail: `${locationName} ${t('HAS_BEEN_REMOVED_FROM_YOUR_LOCATIONS') || 'has been removed from your locations'}`,
          life: 4000
        })
      } catch (error) {
        console.error('Error removing location:', error)
        toast.current?.show({
          severity: 'error',
          detail: t('FAILED_TO_REMOVE_LOCATION') || 'Failed to remove location. Please try again.',
          life: 4000
        })
      }
    },
    [otherLocationsData, t]
  )

  const weatherTypeData = {
    fortnightly: {
      header: `${t('DAYS_FORECAST')}`,
      body: ` ${t('WEATHER_FORECAST_DATA_IS_NOT_AVAILABLE_FOR')}`,
      classNameContainer: 'forecast-header',
      classNameButton: 'forecast-header-button'
    },
    hourly: {
      header: `${t('HOURLY_FORECAST')}`,
      body: `${t('HOURLY_FORECAST_DATA_IS_NOT_AVAILABLE_FOR')}`,
      classNameContainer: 'today-forecast-btn-container',
      classNameButton: 'today-forecast-btn'
    }
  }

  const WeatherNoDataCard = ({ weatherType }) => (
    <>
      {weatherType && <div className={`${weatherTypeData[weatherType].classNameContainer}`}>{weatherTypeData[weatherType].classNameButton && <button className={`${weatherTypeData[weatherType].classNameButton}`}>{weatherTypeData[weatherType].header}</button>}</div>}
      <div className="weather-card no-data-card">
        <div className="no-data-icon">
          {type === 'other-locations' ? (
            <i className="pi pi-search" />
          ) : (
            (() => {
              const icon = getWeatherConditionIcon('Cloudy')

              return typeof icon === 'string' ? <img src={icon} alt="Weather" className="weather-animation" /> : <Lottie animationData={icon} loop={true} className="weather-animation" />
            })()
          )}
        </div>
        {type === 'other-locations' ? (
          <div className="no-data-message">Please search and add a location to get started.</div>
        ) : (
          <p className="no-data-message">
            {weatherTypeData[weatherType].body}
            <strong>{currentCity}</strong>
          </p>
        )}
      </div>
    </>
  )

  const OtherLocationsNoDataCard = ({ duration }) => (
    <>
      {duration && (
        <>
          <div className={`${duration === 'FortNight' ? 'forecast-header' : 'today-forecast-btn-container'}`}>
            <button className={`${duration === 'FortNight' ? 'forecast-header-button' : 'today-forecast-btn'}`}>{duration === 'FortNight' ? '10 DAYS FORECAST' : 'HOURLY FORECAST'}</button>
          </div>
        </>
      )}
      <div className="weather-card no-data-card">
        <div className="no-data-icon">
          <i className="pi pi-search" />
        </div>
        <p className="no-data-message">
          {!searchQuery && (
            <>
              Please search and add a location to get <strong>{duration === 'FortNight' ? 'Weather Forecast' : 'Hourly Forecast'}</strong> data.
            </>
          )}
          {searchQuery && <span className="no-data-message-search">No results found for your search query</span>}
        </p>
      </div>
    </>
  )

  const LocationCardSkeleton = () => (
    <div className="location-card skeleton-card">
      <div className="location-card-header">
        <div className="location-date-time-wrapper">
          <Skeleton width="80px" height="18px" className="skeleton-text custom-skelton" />
          <Skeleton width="60px" height="18px" className="skeleton-text custom-skelton" />
        </div>
        <div className="location-flag-wrapper">
          <Skeleton width="60px" height="18px" className="skeleton-text custom-skelton" />
        </div>
        <Skeleton width="80px" height="48px" className="skeleton-temp custom-skelton" />
      </div>
      <div className="location-card-body">
        <Skeleton width="150px" height="18px" className="skeleton-text custom-skelton" />
        <Skeleton width="100px" height="16px" className="skeleton-text custom-skelton" />
      </div>
      <div className="location-card-footer">
        <Skeleton width="100px" height="16px" className="skeleton-text custom-skelton" />
        <Skeleton width="80px" height="18px" className="skeleton-text custom-skelton" />
        <Skeleton width="80px" height="18px" className="skeleton-text custom-skelton" />
      </div>
    </div>
  )

  const ForecastItemSkeleton = () => (
    <div className="forecast-item skeleton-forecast-item">
      <Skeleton width="50px" height="18px" className="skeleton-text" />
      <div className="forecast-icon-wrapper">
        <Skeleton shape="circle" width="40px" height="40px" className="skeleton-icon" />
        <Skeleton width="20px" height="20px" className="skeleton-per" />
      </div>
      <Skeleton width="100%" height="6px" className="skeleton-bar" />
      <Skeleton width="30px" height="13px" className="skeleton-text" />
    </div>
  )

  const HourlyForecastSkeleton = () => (
    <div className="hour-row skeleton-hour-row">
      <div className="hourly-forecast-item">
        <Skeleton width="40px" height="12px" className="skeleton-text" />
        <Skeleton shape="circle" width="30px" height="30px" className="skeleton-icon" />
        <Skeleton width="30px" height="14px" className="skeleton-text" />
      </div>
    </div>
  )

  const GraphSkeleton = () => (
    <div className="temperature-graph-container skeleton-graph">
      <div className="graph-icons-row">
        {Array.from({ length: SKELETON_COUNT.GRAPH_ITEM }).map((_, index) => (
          <Skeleton shape="circle" width="24px" height="24px" key={index} className="skeleton-icon" />
        ))}
      </div>
      <Skeleton width="100%" height="250px" className="skeleton-chart" />
    </div>
  )

  const getActiveLocation = () => {
    const locationsSource = isOtherOptionsOpen ? otherLocationsData : weatherData

    if (!locationsSource?.length || !currentCity) return null

    return locationsSource.find((el) => el?.location_name === currentCity) || null
  }

  const getCurrentTemperature = () => {
    const activeLocation = getActiveLocation()

    return activeLocation?.weather?.temperature
  }

  const getHourlyForecast = ({ forecast_time: forecastTime, temperature }) => {
    const formatedTime = getHourlyTime(forecastTime)
    const forecastHour = convertTo24Hour(formatedTime)

    const activeLocation = getActiveLocation()
    const timezone = activeLocation?.timezone

    let isNow = false

    if (timezone) {
      const currentTimeInLocation = getCurrentTimeForTimezone(timezone)
      const currentHourInLocation = convertTo24Hour(currentTimeInLocation)

      isNow = currentHourInLocation === forecastHour
    }

    let timeStamp = formatedTime
    let currentTemp = temperature

    if (isNow && currentDay === 'Today') {
      timeStamp = 'Now'
      const latestTemp = getCurrentTemperature()

      if (latestTemp !== undefined && latestTemp !== null) {
        currentTemp = latestTemp
      }
    }

    return { timeStamp, currentTemp }
  }

  return (
    <div className="weather-screen">
      <div className="details-header-container">
        <div className="details-header-title">{isOtherOptionsOpen ? 'Other Locations' : 'Coffee Weather'}</div>
        <div className="details-header">
          <button className={`other-locations-btn ${isOtherOptionsOpen ? 'active' : ''}`} onClick={handleOtherOption}>
            Other Locations
          </button>
          <CustomDropdown disabled={isLoadingLocations} value={selectedCountry} onChange={(e) => handleDropdownChange(e, 'country')} options={uniqueCountries} optionLabel="country_name" showFlags={true} filter={true} placeholder="Select Country" className="weather-country-dropdown" panelClassName="weather-country-dropdown-panel" />
          <CustomDropdown disabled={isOtherOptionsOpen || isLoadingLocations} value={selectedState} onChange={(e) => handleDropdownChange(e, 'state')} options={uniqueStates} optionLabel="state" filter={true} placeholder="Select State" className="weather-country-dropdown" />
          <CustomDropdown disabled={isOtherOptionsOpen || isLoadingLocations} value={selectedRegion} onChange={(e) => handleDropdownChange(e, 'region')} options={uniqueRegions} optionLabel="region" filter={true} placeholder="Select Region" className="weather-country-dropdown" />
        </div>
      </div>
      <div className="weather-content-container">
        <div className="weather-main-container">
          {/* Left Column - Current Locations (memoized to avoid re-render when forecast/hourly state changes) */}
          <WeatherLocationsColumn isLoadingLocations={isLoadingLocations} isSearching={isSearching} isOtherOptionsOpen={isOtherOptionsOpen} selectedCountry={selectedCountry} selectedState={selectedState} selectedRegion={selectedRegion} weatherData={weatherData} setWeatherData={setWeatherData} currentCity={currentCity} otherLocationsData={otherLocationsData} savedLocationsFound={savedLocationsFound} searchQuery={searchQuery} userDetails={userDetails} onLocationClick={handleLocationClick} onCountrySearch={handleCountrySearch} onAddLocation={handleAddLocation} onRemoveLocation={handleRemoveLocation} getCountryFlag={getCountryFlag} getCurrentDateForTimezone={getCurrentDateForTimezone} getCurrentTimeForTimezone={getCurrentTimeForTimezone} getWeatherConditionIcon={getWeatherConditionIcon} getWeatherLottieAsset={getWeatherLottieAsset} />

          {/* Middle Column - 14 Days Forecast */}
          <div className="weather-forecast-column">
            <div className="forecast-list">
              {isLoadingForecast ? (
                <>
                  <div className="location-card-header-skeleton">
                    <Skeleton width="90%" height="18px" className="skeleton-text custom-skelton" />
                  </div>
                  {Array.from({ length: SKELETON_COUNT.FORECAST_ITEM }).map((_, index) => (
                    <ForecastItemSkeleton key={index} />
                  ))}
                </>
              ) : (
                <>
                  {fortNightForecast.length > 0 ? (
                    <>
                      <div className="forecast-header">
                        <button className="forecast-header-button">10 Days Forecast</button>
                      </div>
                      {fortNightForecast.map((forecast, index) => (
                        <div key={index} className={`forecast-item ${currentForecast === forecast?.forecast_date ? 'active' : ''} ${theme === 'light' && currentForecast === forecast?.forecast_date ? 'light-theme' : 'dark-theme'}`} onClick={() => handleFortNightForecastClick(forecast?.location_id, forecast?.forecast_date)}>
                          <div className="forecast-day">
                            <div>{currentForecast === forecast?.forecast_date && <input type="radio" name="forecast" value={forecast?.forecast_date} defaultChecked />}</div>
                            {getDayOfWeek(forecast?.forecast_date)}
                          </div>

                          <div className="forecast-icon-wrapper">
                            {(() => {
                              const icon = getWeatherConditionIcon(forecast?.weather_condition)

                              return typeof icon === 'string' ? <img src={icon} alt={forecast?.weather_condition || 'Weather'} className="weather-animation" /> : <Lottie animationData={icon} loop={true} className="weather-animation" />
                            })()}
                            <div className="rain-chance-badge">{forecast?.precipitation_probability}%</div>
                          </div>
                          <div className="forecast-temp-low">{Number(forecast?.low_temperature).toFixed(0)}°C</div>
                          <div className="forecast-temp-bar">
                            <div
                              className="forecast-temp-bar-fill"
                              style={{
                                width: forecast?.high_temperature != null && forecast?.low_temperature != null ? `${((forecast.high_temperature - forecast.low_temperature) / 20) * 100}%` : '0%'
                              }}
                            />
                          </div>
                          <div className="forecast-temp-high">{Number(forecast?.high_temperature).toFixed(0)}°C</div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <>{isOtherOptionsOpen ? <OtherLocationsNoDataCard duration={'FortNight'} /> : <WeatherNoDataCard weatherType="fortnightly" />}</>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Right Column - Hourly Forecast & Graph */}
          <div className="weather-details-column">
            {isLoadingHourly ? (
              <>
                <div className="location-card-header-skeleton">
                  <Skeleton width="90%" height="18px" className="skeleton-text custom-skelton" />
                </div>
                {Array.from({ length: SKELETON_COUNT.HOURLY_FORECAST_SECTION }).map((_, index) => (
                  <div className="hourly-forecast-section custom-skeleton" key={index}>
                    {Array.from({ length: SKELETON_COUNT.HOURLY_FORECAST_ITEM }).map((_, index) => (
                      <HourlyForecastSkeleton key={index} />
                    ))}
                  </div>
                ))}
              </>
            ) : (
              <>
                {hourlyForecast.length > 0 ? (
                  <>
                    <div className="today-forecast-btn-container">
                      <button className="today-forecast-btn">{currentDay}</button>
                    </div>
                    <div className="hourly-forecast-section">
                      {hourlyForecast.map((row, rowIndex) => (
                        <div key={rowIndex} className="hour-row">
                          <div className={`hourly-forecast-item ${getHourlyForecast(row)?.timeStamp === 'Now' ? 'now-frame' : ''}`}>
                            <div className={`hourly-time ${getHourlyForecast(row)?.timeStamp === 'Now' ? 'now' : ''}`}>{getHourlyForecast(row)?.timeStamp}</div>
                            {(() => {
                              const icon = getWeatherConditionIcon(row.weather_condition)

                              return typeof icon === 'string' ? <img src={icon} alt={row.weather_condition || 'Weather'} className="weather-animation-small" /> : <Lottie animationData={icon} loop={true} className="weather-animation-small" />
                            })()}
                            <div className="hourly-temp">{Number(getHourlyForecast(row)?.currentTemp).toFixed(0)}°C</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <>{isOtherOptionsOpen ? <OtherLocationsNoDataCard duration={'Hourly'} /> : <WeatherNoDataCard weatherType="hourly" />}</>
                )}
              </>
            )}

            {isLoadingHourly ? (
              <GraphSkeleton />
            ) : (
              <>
                {hourlyForecast.length > 0 && (
                  <div className="temperature-graph-container">
                    <div className="graph-icons-row">
                      <div className="graph-icon">{getWeatherIcon('cloud-moon')}</div>
                      <div className="graph-icon">{getWeatherIcon('sun')}</div>
                      <div className="graph-icon">{getWeatherIcon('cloud')}</div>
                      <div className="graph-icon">{getWeatherIcon('cloud')}</div>
                    </div>
                    <div className="graph-wrapper">
                      <Chart type="line" data={temperatureData} options={chartOptions} />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <Toast ref={toast} position="top-right" className="weather-toast" />
    </div>
  )
}

export default Weather
